import { useThrottleFn } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getDeviceAuthorityInfo, getDeviceInfo, getDeviceTelemetries } from 'src/services/device/common/deviceUtils';
import AdjustmentOverlay from 'src/ui/AdjustmentOverlay/AdjustmentOverlay';
import 'gamecontroller.js';
import useDeviceTelemetries from 'src/services/device/common/useDeviceTelemetries';
import { useCameraSource } from 'src/helper/CameraSourceProvider';
import { useOnboardHub } from 'src/components/Dashboard/Components/OnboardHubProvider/OnboardHubProvider';
import { useDeviceInfo } from 'src/helper/DeviceInfoProvider';
import useDeviceFlightAuthority from 'src/services/device/common/useDeviceFlightAuthority';
import { DjiFlightJoystickAuthorityMode } from 'src/services/device/common/deviceConstants';
import { setDeviceFlightAuthorityStatus } from 'src/services/device/DeviceServiceSlice';
import useCloudLiveFlight from 'src/services/mission/common/useCloudLiveFlight';

const DRC_HORIZONTAL_MAX_SPEED = 380;
const DRC_VERTICAL_MAX_SPEED = 110;
const DRC_YAW_MAX_SPEED = 320;
const DRC_SPEED_RATIO_MIN = 1;
const DRC_SPEED_RATIO_MAX = 10;

function OnboardManualFlightShortcuts() {
  const disableKeyboardEvents = useSelector(state => state.ui.disableKeyboardEvents);
  const store = useStore();
  const dispatch = useDispatch();
  const { hub, groups, deviceId, joinCompleted } = useOnboardHub();
  const deviceInfo = useDeviceInfo(deviceId);
  const flightController = useCloudLiveFlight(deviceId);
  const flightAuthority = useDeviceFlightAuthority(deviceInfo?.attachedToDock || deviceId);
  const { activeCameraPayload } = useDeviceInfo();
  const [adjustmentOverlayConfig, setAdjustmentOverlayConfig] = useState();
  const lastLocalValuesRef = useRef({});
  const drcControlValuesRef = useRef({});
  const drcControlSeqRef = useRef(0);
  const drcIntervalIdRef = useRef(null);
  const drcSpeedRatioRef = useRef(DRC_SPEED_RATIO_MAX);

  const checkFlightAuthority = () => {
    if(flightAuthority?.authorityMode !== DjiFlightJoystickAuthorityMode.PSDK) {
      const info = getDeviceInfo(deviceId);
      const parentDeviceId = info?.attachedToDock;

      dispatch(setDeviceFlightAuthorityStatus({
        deviceId: parentDeviceId || deviceId,
        data: {
          requiredAuthorityError: DjiFlightJoystickAuthorityMode.PSDK,
        }
      }));
    }
  }

  const { run: throttledHandleKeydown } = useThrottleFn((event) => {
    const shift = !!event.shiftKey;
    var key = event.code.replace("Arrow", ""); // ArrowUp -> Up, etc... for Edge and IE 11

    if(shift && (key === "Up" || key === "Down" || key === "Right" || key === "Left")){
      const ratio = 15;
      const pitchMultiply = key === "Up" ? 1 : key === "Down" ? -1 : 0;
      const yawMultiply = key === "Left" ? -1 : key === "Right" ? 1 : 0;

      if (joinCompleted) {
        hub?.sendToGroup(deviceId, groups?.send?.commandChannel, {
          gimbalSpeed: {
            payloadIndex: activeCameraPayload?.payloadIndex,
            pitch: ratio * pitchMultiply,
            roll: 0,
            yaw: ratio * yawMultiply,
          }
        });
      }
    }

    if(key === "KeyR"){
      hub?.sendToGroup(deviceId, groups?.send?.commandChannel, {
        gimbalReset: {
          payloadIndex: activeCameraPayload?.payloadIndex,
          status: true,
        }
      });
    }

    if(key === "KeyX"){
      drcSpeedModeRef.current = (drcSpeedModeRef.current + 1) % 3;
    }

    if(shift && key === "KeyO"){
      flightAuthority?.requestPsdkAuthority(deviceId);
    }

    if(key === "KeyQ" || key === "KeyE"){
      const ratio = 8;
      const maxZoomFactor = 200;
      const minZoomFactor = 2;
      const zoomMultiply = key === "KeyE" ? 1 : -1;
      const currZoomFactor = store.getState().flight.cameraStats?.zoomFactor || 1;
      let newZoomFactor = Math.round((lastLocalValuesRef.current.zoom_factor || currZoomFactor) + (ratio * zoomMultiply));

      if(newZoomFactor > maxZoomFactor) newZoomFactor = maxZoomFactor;
      if(newZoomFactor < minZoomFactor) newZoomFactor = minZoomFactor;

      setAdjustmentOverlayConfig({
        minValue: minZoomFactor,
        maxValue: maxZoomFactor,
        value: newZoomFactor,
        title: 'Zoom Factor'
      });

      lastLocalValuesRef.current.zoom_factor = newZoomFactor;

      if (joinCompleted) {
        hub?.sendToGroup(deviceId, groups?.send?.commandChannel, {
          zoomFactor: {
            payloadIndex: activeCameraPayload?.payloadIndex,
            zoomFactor: parseFloat(newZoomFactor),
          }
        });
      }
    }

    if(key === "KeyZ" || key === "KeyC"){
      let newSpeedRatio = drcSpeedRatioRef.current + (key === "KeyC" ? 1 : -1);

      if(newSpeedRatio > DRC_SPEED_RATIO_MAX) newSpeedRatio = DRC_SPEED_RATIO_MAX;
      if(newSpeedRatio < DRC_SPEED_RATIO_MIN) newSpeedRatio = DRC_SPEED_RATIO_MIN;

      setAdjustmentOverlayConfig({
        minValue: DRC_SPEED_RATIO_MIN,
        maxValue: DRC_SPEED_RATIO_MAX,
        value: newSpeedRatio,
        title: 'Speed Ratio'
      });

      drcSpeedRatioRef.current = newSpeedRatio;
    }
  }, { wait: 500 });

  const drcHandleKeydown = (event) => {
    const shift = !!event.shiftKey;
    const controlValues = { ...drcControlValuesRef.current };
    const key = event.code.replace("Arrow", "");
    const normalizeDrcValue = (value) => value != undefined ? parseFloat(value.toFixed(2)) : 0;

    // Skip alternative shortcuts
    if(shift) return;

    // ratio based on drcSpeedMode
    const horizontalRatio = (drcSpeedRatioRef.current /10);
    const verticalRatio = (drcSpeedRatioRef.current / 10);
    const rotateRatio = 0.2;

    if(['KeyW', 'KeyS', 'KeyA', 'KeyD', 'Up', 'Down', 'Left', 'Right'].includes(key)) checkFlightAuthority();

    switch(key) {
      case 'KeyW':
        controlValues.x = normalizeDrcValue(DRC_HORIZONTAL_MAX_SPEED * horizontalRatio);
        break;
    
      case 'KeyS':
        controlValues.x = normalizeDrcValue(-DRC_HORIZONTAL_MAX_SPEED * horizontalRatio);
        break;
    
      case 'KeyA':
        controlValues.yaw = normalizeDrcValue(-DRC_YAW_MAX_SPEED * rotateRatio);
        break;
    
      case 'KeyD':
        controlValues.yaw = normalizeDrcValue(DRC_YAW_MAX_SPEED * rotateRatio);
        break;
    
      case 'Up':
        controlValues.z = normalizeDrcValue(DRC_VERTICAL_MAX_SPEED * verticalRatio);
        break;
    
      case 'Down':
        controlValues.z = normalizeDrcValue(-DRC_VERTICAL_MAX_SPEED * verticalRatio);
        break;
    
      case 'Left':
        controlValues.y = normalizeDrcValue(-DRC_HORIZONTAL_MAX_SPEED * horizontalRatio);
        break;

      case 'Right':
        controlValues.y = normalizeDrcValue(DRC_HORIZONTAL_MAX_SPEED * horizontalRatio);
        break;
    }

    // check if any of prev values is different from current values
    if(Object.keys(controlValues).some(key => controlValues[key] !== drcControlValuesRef.current[key])) {
      drcControlSeqRef.current = 0;
    }

    drcControlValuesRef.current = controlValues;
  }

  const handleKeydown = useCallback((event) => {
    throttledHandleKeydown(event);
    drcHandleKeydown(event);
  }, [throttledHandleKeydown]);

  const handleKeyUp = (event) => {
    const key = event.code.replace("Arrow", "");
    const controlValues = { ...drcControlValuesRef.current };

    if(key === 'KeyW' || key === 'KeyS') {
      delete controlValues.x;
    }

    if(key === 'KeyA' || key === 'KeyD') {
      delete controlValues.yaw;
    }

    if(key === 'Up' || key === 'Down') {
      delete controlValues.z;
    }

    if(key === 'Left' || key === 'Right') {
      delete controlValues.y;
    }

    drcControlValuesRef.current = controlValues;
  }

  useEffect(() => {
    document.removeEventListener('keydown', handleKeydown);
    document.addEventListener('keyup', handleKeyUp);

    // if(!disableKeyboardEvents) {
      document.addEventListener('keydown', handleKeydown);
      document.addEventListener('keyup', handleKeyUp);
    // }

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [disableKeyboardEvents, handleKeydown]);
  
  useEffect(() => {
    clearInterval(drcIntervalIdRef.current);

    drcIntervalIdRef.current = setInterval(() => {
      if(Object.keys(drcControlValuesRef.current).length) {
        hub?.sendToGroup(deviceId, groups?.send?.commandChannel, {
          joystickControl: {
            x: 0,
            y: 0,
            z: 0,
            yaw: 0,
            ...drcControlValuesRef.current,
          }
        });
      }
    }, 300);

    return () => clearInterval(drcIntervalIdRef.current);
  }, []);

  return adjustmentOverlayConfig ? (
    <AdjustmentOverlay
      min={adjustmentOverlayConfig.minValue} 
      max={adjustmentOverlayConfig.maxValue} 
      value={adjustmentOverlayConfig.value}
      title={adjustmentOverlayConfig.title}
    />
  ) : (
    <div></div>
  )
}

export default OnboardManualFlightShortcuts